import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { rem } from "polished"
import { media } from "@sr/common"
import SanityImage from "gatsby-plugin-sanity-image"

import {
  BdTitle3,
  SanityImg,
  BlockContentInline,
  Button,
  MediaVimeo,
  IconClose,
} from "@sr/gatsby-theme-ui"

import { Link } from "gatsby"
import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
} from "body-scroll-lock-upgrade"

export interface ProductHeroProps {
  hero: {
    buttonText: string
    buttonLink: {
      reference: {
        path: {
          current: string
        }
      }
    }
    videoButtonLink: string
    videoButtonText: string
    img: SanityImg
    _rawTextBlock: any
    title: string
  }
}

const ProductHero = ({ hero }: ProductHeroProps) => {
  const [videoOpen, setVideoOpen] = useState(false)
  const dialogRef = useRef<any>(null)

  useEffect(() => {
    const handleCloseModal = (e: any) => {
      if (
        e.target.classList.contains("product-hub__video-dialog") ||
        e.target.classList.contains("close")
      ) {
        setVideoOpen(false)
      }
    }

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setVideoOpen(false)
      }
    }

    if (videoOpen) {
      document.addEventListener("click", handleCloseModal)
      document.addEventListener("keydown", handleEsc)
    }
    return () => {
      document.removeEventListener("click", handleCloseModal)
      document.removeEventListener("keydown", handleEsc)
    }
  }, [videoOpen])

  useEffect(() => {
    if (dialogRef && dialogRef.current) {
      disableBodyScroll(dialogRef.current)
    }

    return () => clearAllBodyScrollLocks()
  }, [videoOpen])

  if (
    !hero?.title ||
    !hero?.buttonLink?.reference?.path?.current ||
    !hero?.buttonText
  )
    return null

  return (
    <ProductHeroStyled>
      {videoOpen && (
        <dialog open className="product-hub__video-dialog" ref={dialogRef}>
          <div className="product-hub__video-dialog-close-container">
            <div className="product-hub__video-dialog-close">
              <IconClose className="close" />
            </div>
          </div>
          {hero?.videoButtonLink && (
            <div className="product-hub__video-container">
              <MediaVimeo
                className="product-hub__video"
                controls={true}
                responsive={true}
                playing={true}
                url={hero?.videoButtonLink}
              />
            </div>
          )}
        </dialog>
      )}
      <div className="product-hub-hero__outer-container">
        <div className="product-hub-hero__image-container">
          {hero?.img?.asset && (
            <SanityImage
              alt={hero?.img.alt || "hero image"}
              asset={hero?.img.asset}
              className="product-hub-hero__image"
              crop={hero?.img.crop}
              hotspot={hero?.img.hotspot}
            />
          )}
        </div>
        <div className="product-hub-hero__text-container">
          <BdTitle3>{hero?.title}</BdTitle3>
          <BlockContentInline blocks={hero?._rawTextBlock} />
          <div className="product-hub-hero__btn-container">
            <Button expand btnColor="var(--color-primary)">
              <Link
                to={hero?.buttonLink?.reference?.path?.current}
                className="product-hub-hero__learn-more-link"
              >
                {hero?.buttonText}
              </Link>
            </Button>
            {hero?.videoButtonLink && hero?.videoButtonText && (
              <Button expand onClick={() => setVideoOpen(true)}>
                {hero?.videoButtonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </ProductHeroStyled>
  )
}

export default ProductHero

const ProductHeroStyled = styled.section`
  width: 100%;
  display: flex;
  justify-content: center;

  .product-hub-hero__outer-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: ${rem(48)} ${rem(16)} 0;
    border-radius: ${rem(8)};
    box-shadow: 0px 4px 8px rgba(45, 50, 57, 0.2);
    overflow: hidden;
    max-width: ${rem(1312)};

    @media ${media.midUp} {
      margin: ${rem(48)} ${rem(64)} 0;
    }

    @media ${media.stMidUp} {
      flex-direction: row;
      justify-content: center;
      align-items: center;
      margin-top: ${rem(96)};
    }
  }

  .product-hub-hero__image-container {
    width: 100%;

    @media ${media.stMidUp} {
      height: ${rem(576)};
      width: 50%;

      .product-hub-hero__image {
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .product-hub-hero__text-container {
    margin: ${rem(40)} ${rem(16)};

    @media ${media.midUp} {
      margin: ${rem(56)} ${rem(40)} ${rem(64)};
    }

    @media ${media.stMidUp} {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: ${rem(656)};
      margin: 0;
      padding-left: ${rem(56)};
      height: 100%;
    }

    h3 {
      max-width: ${rem(400)};
      color: var(--color-primary);

      @media ${media.midUp} {
        font-size: ${rem(40)};
        line-height: ${rem(48)};
      }
    }
    p {
      margin-top: ${rem(24)};

      @media ${media.stMidUp} {
        max-width: ${rem(552)};
        font-size: ${rem(16)};
      }
    }

    .product-hub-hero__btn-container {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      gap: ${rem(16)};
      width: 100%;
      padding-top: ${rem(24)};

      @media ${media.midUp} {
        flex-direction: row;
      }

      @media ${media.stMidUp} {
        max-width: ${rem(552)};
      }

      .product-hub-hero__learn-more-link {
        color: var(--color-white);
        text-decoration: none;
      }
    }
  }
  .product-hub__video-dialog {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: #2d32388d;
    z-index: 10;

    .product-hub__video-dialog-close-container {
      width: 80%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    .product-hub__video-dialog-close {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${rem(40)};
      height: ${rem(40)};
      right: ${rem(-25)};
      top: ${rem(10)};

      background: var(--color-white);
      border-radius: 50%;
      box-shadow: 0px 4px 8px 0px rgba(45, 50, 57, 0.2);

      .close {
        cursor: pointer;
      }
    }

    .product-hub__video-container {
      width: 80%;
      max-height: 80%;
      padding: ${rem(12)};

      @media ${media.stUp} {
        padding: ${rem(20)};
      }

      .product-hub__video {
        width: 100%;
        height: 100%;
        aspect-ratio: 16 / 9;

        iframe {
          width: 100% !important;
          height: 100% !important;
        }
      }
    }
  }
`
